import { Button, Flex, Tooltip } from '@mantine/core'
import { useEffect, useState } from 'react'

import { useAppDispatch } from '@flecto-io/core'
import getLoggedUserAddress from '@flecto-io/core/src/actions/user/getLoggedUserAddress'
import { useAnalytics } from '@flecto/analytics'
import { useUserVerified } from '@flecto/auth'
import { useQueryGetAllItems } from '@flecto/core/saas'
import { useFeatureFlagValue } from '@flecto/feature-flags'
import { useTranslation } from '@flecto/translations'
import type { CoreItem } from '@flecto/types'
import { Page } from '@flecto/ui/saas'

import { useTaxesInfo } from '../../taxes'
import { FiltersItems } from '../components/FiltersItems/FiltersItems'
import { TableItems } from '../components/TableItems/TableItems'
import { useDrawerItem } from '../hooks/useDrawerItem'
import { PageItemsListV2 } from './PageItemsListV2'

export function PageItemsList() {
  const isBundlesFFEnabled = useFeatureFlagValue('SAAS_ENABLE_BUNDLES')
  if (isBundlesFFEnabled) {
    return <PageItemsListV2 />
  }

  const [selectedItem, setSelectedItem] = useState<
    Partial<CoreItem> | undefined
  >(undefined)

  const track = useAnalytics('segment')
  const { t: generalTranslation } = useTranslation('saas', {
    keyPrefix: 'general',
  })
  const { t } = useTranslation('saas', { keyPrefix: 'items' })

  const pageSize = 10
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRecords, setTotalRecords] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [items, setItems] = useState<CoreItem[]>([])

  const { data, isLoading, isFetched } = useQueryGetAllItems({
    q: searchTerm,
    limit: pageSize,
    offset: currentPage * pageSize - pageSize,
    order: 'desc',
  })

  const { isLoading: isLoadingTaxes } = useTaxesInfo()

  const { openDrawer } = useDrawerItem({
    item: selectedItem,
    onError: (message: string) => {
      track('ITEM__CREATED_ATTEMPT', {
        error: true,
        success: false,
        errorMessage: message,
      })
    },
    onClose: () => {
      setSelectedItem(undefined)
      track('ITEM__CREATED_ATTEMPT', { error: false, success: false })
    },
    onSuccess: () => {
      setSelectedItem(undefined)
    },
  })

  const dispatch = useAppDispatch()

  const { isUserOnboarded, isUserVerified } = useUserVerified()

  const isDisabled = !isUserOnboarded || !isUserVerified

  const scheduleAddresses = () => {
    setTimeout(() => {
      dispatch(getLoggedUserAddress())
    }, 0)
  }

  const handleStartCreateItem = () => {
    setSelectedItem({})
    track('ITEM__NEW', {})
    scheduleAddresses()
  }

  const handleEditItem = (item: CoreItem) => {
    setSelectedItem(undefined)
    setSelectedItem(item)
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  const handleSearch = (term: string) => {
    setCurrentPage(1)
    setSearchTerm(term)
  }

  useEffect(() => {
    if (selectedItem) {
      openDrawer()
    }
  }, [selectedItem, openDrawer])

  useEffect(() => {
    scheduleAddresses()

    return () => setSelectedItem(undefined)
  }, [])

  useEffect(() => {
    if (isFetched) {
      setTotalRecords(data?.total_count ?? 0)
      setItems(data?.item ?? [])
    }
  }, [data, isFetched])

  return (
    <Page>
      <Page.Title>{t('page_title')}</Page.Title>
      <Page.Actions>
        <Tooltip
          disabled={!isDisabled}
          label={generalTranslation(
            'shared.layout.notification_user_need_vefify'
          )}
          position='bottom'
        >
          <Flex gap='sm'>
            <Button
              data-cy='add-new-item'
              data-testid='btn-new-product'
              variant='filled'
              disabled={isDisabled}
              onClick={handleStartCreateItem}
            >
              {t('add_item_label')}
            </Button>
          </Flex>
        </Tooltip>
      </Page.Actions>
      <Page.Content>
        <Flex direction='column' gap={'md'}>
          <Flex justify={'flex-end'}>
            <FiltersItems onFilter={handleSearch} />
          </Flex>

          <TableItems
            items={items}
            isLoading={isLoading || isLoadingTaxes}
            onRowClick={({ record }) => handleEditItem(record)}
            withPaginationControlled={{
              currentPage,
              totalRecords,
              onPageChange: handlePageChange,
            }}
          />
        </Flex>
      </Page.Content>
    </Page>
  )
}
